import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { useCallback, useEffect, useMemo } from 'react';
import { Control, useController, UseControllerProps, useForm, useWatch } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { FieldTransformer } from './FieldTransformers/FieldTransformers';
import { fromPageQuery, omitEmptyValues, parseFilterQuery, stringifyFilters, toPageQuery, toRequestQuery } from './queryTransformers';
import { getDefaultCommonDataFilterValues, useSaveCommonFieldsToLocalStorage } from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import analytics from '../../../shared/services/analytics/analytics';
import { DATASET_FILTERS_ANALYTICS_KEYS, formatOrderEventName, useDataExplorerAnalyticsPage } from './dataExplorerAnalytics';
import { auxiliaryFields } from './auxiliaryFields';
import { useDataExplorerHistory } from '../dataExplorerHistoryContext';

interface Props {
  fieldTransformers: FieldTransformer[];
  defaultValues?: { region?: string; delay?: string; universe?: string; instrument?: string };
  debounce?: number;
}

export const useFilterForm = ({ fieldTransformers, defaultValues: parentDefaultValues = {}, debounce = 200 }: Props) => {
  const history = useDataExplorerHistory();
  const oldQueryString = history.location.search;
  const oldQueryObject = useMemo(() => parseFilterQuery(oldQueryString), [oldQueryString]);

  let filteredParentDefaultValues = parentDefaultValues;
  if (oldQueryObject.region && oldQueryObject.region !== parentDefaultValues?.region) {
    let { universe, ...otherValues } = parentDefaultValues;
    filteredParentDefaultValues = otherValues;
  }

  const defaultValues = useMemo(
    () => merge({}, filteredParentDefaultValues, fromPageQuery(oldQueryObject, fieldTransformers)),
    // react only on parentDefaultValues because now we don't want to update form live from url
    [parentDefaultValues]
  ) as any;

  // save query that is unused by this filter to restore it later
  const unusedQueryObject = useMemo(() => {
    let result = cloneDeep(oldQueryObject);

    const keysToRemove = Object.keys(toPageQuery(filteredParentDefaultValues, fieldTransformers));

    for (const key of keysToRemove) {
      delete result[key];
    }

    return result;
  }, [oldQueryObject, filteredParentDefaultValues]);

  const { control, reset, setValue, getValues } = useForm({ defaultValues });

  const values = useWatch({ control });

  // create new page query object with form filter values and restored unused query values
  const pageQueryObject = useMemo(() => omitEmptyValues(merge({}, unusedQueryObject, toPageQuery(values, fieldTransformers))), [values]);
  const [debouncedPageQueryObject, debouncePageControl] = useDebounce(pageQueryObject, debounce);
  const pageQueryString = stringifyFilters(debouncedPageQueryObject);

  // create new request query object with form filter values and restored unused query values
  const requestQueryObject = useMemo(
    () => omitEmptyValues(merge({}, unusedQueryObject, toRequestQuery(values, fieldTransformers))),
    [values]
  );
  const [debouncedRequestQueryObject, debounceRequestControl] = useDebounce(requestQueryObject, debounce);

  const flushDebounce = useCallback(() => {
    //  use setTimeout so React will update values before flushing
    setTimeout(() => {
      debounceRequestControl.flush();
      debouncePageControl.flush();
    }, 0);
  }, [debounceRequestControl, debouncePageControl]);

  useEffect(() => {
    history.replace({ search: pageQueryString });
  }, [pageQueryString]);

  useSaveCommonFieldsToLocalStorage(pageQueryObject);

  const filterControl = useMemo(() => ({ ...control, setValue, flushDebounce, getValues }), [control, flushDebounce]);

  const onResetFilters = useCallback(() => {
    // don't change common fields on reset
    reset(merge({}, parentDefaultValues, getDefaultCommonDataFilterValues()));
    flushDebounce();
  }, [parentDefaultValues, flushDebounce]);

  return {
    reset: onResetFilters,
    control: filterControl,
    debouncedQueryObject: debouncedRequestQueryObject,
    flushDebounce,
    values,
  };
};

export const useFiltersChanged = ({
  control,
  fieldNames,
  defaultValues,
}: {
  control: Control;
  fieldNames: string[];
  defaultValues: any;
}) => {
  const values = useWatch({ control });
  const isActive = useMemo(() => !isEqual(pick(values, fieldNames), pick(defaultValues, fieldNames)), [values, defaultValues]);

  return isActive;
};

// useFilterController is used to inject custom logic like analytics in onChange handlers
export const useFilterController = (controllerProps: UseControllerProps) => {
  const controller = useController(controllerProps);
  const page = useDataExplorerAnalyticsPage();
  const onChange = useCallback(
    (...args: any[]) => {
      controller.field.onChange(...args);
      let eventName = DATASET_FILTERS_ANALYTICS_KEYS[controllerProps.name];

      if (controllerProps.name === auxiliaryFields.order.name) {
        const value = args[0]?.target?.value;
        eventName = formatOrderEventName(value) as any;
      }
      analytics.trackCustomEvent(eventName, { event_label: page });
    },
    [controller.field.onChange, page]
  );

  const filterController = useMemo(
    () => ({
      ...controller,
      // onChangeOriginal is used when we change field value in effects or we don't want to use analytics
      field: { ...controller.field, onChange, onChangeOriginal: controller.field.onChange },
    }),
    [onChange, controller]
  );

  return filterController;
};
